import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '../components/ui/Button.jsx';
import { useTranslation } from '../i18n/useTranslation.js';

/**
 * Die öffentliche Seite hinter einem Einmal-Link.
 *
 * Sie ist ohne Anmeldung erreichbar, weil genau das der Punkt ist: wer hier
 * landet, kommt gerade NICHT mehr hinein.
 *
 * Bewusst ohne jede Auskunft über den Link, solange er nicht eingelöst ist.
 * Die Seite sagt nicht, zu wem er gehört, ob er abgelaufen ist oder ob es ihn
 * überhaupt gibt - erst nach dem erfolgreichen Setzen erscheint die Adresse,
 * für die das Kennwort jetzt gilt. Wer Links durchprobiert, lernt daraus
 * nichts.
 */
export function SetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = repeat.length > 0 && password !== repeat;
  const canSubmit = token && password.length >= 10 && password === repeat && !busy;

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/public/set-password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(body?.error?.message || t('setPassword.failed'));
      }
      setDone(body?.data?.email || '');
    } catch (err) {
      setError(err.message || t('setPassword.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pf-login-wrap">
      <div className="pf-login-card">
        <h1>{t('setPassword.title')}</h1>

        {!token && <div className="alert danger">{t('setPassword.noToken')}</div>}

        {done !== null ? (
          <>
            <div className="alert success">{t('setPassword.done', { email: done })}</div>
            <Link className="btn primary" to="/login">
              {t('setPassword.toLogin')}
            </Link>
          </>
        ) : (
          <form onSubmit={submit}>
            <p className="muted text-sm" style={{ marginTop: 0 }}>
              {t('setPassword.hint')}
            </p>

            <label className="field">
              <span className="field-label">{t('setPassword.new')}</span>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={10}
                autoFocus
                autoComplete="new-password"
              />
              {tooShort && <span className="muted text-xs">{t('setPassword.tooShort')}</span>}
            </label>

            <label className="field">
              <span className="field-label">{t('setPassword.repeat')}</span>
              <input
                className="input"
                type="password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                autoComplete="new-password"
              />
              {mismatch && <span className="muted text-xs">{t('setPassword.mismatch')}</span>}
            </label>

            {error && <div className="alert danger">{error}</div>}

            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {busy ? t('setPassword.saving') : t('setPassword.cta')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
